import jsPDF from 'jspdf';
import type { LeaderInsights } from './surveys-api';

type Ctx = {
  doc: jsPDF;
  x: number;
  y: number;
  w: number;
  pageH: number;
  margin: number;
};

const COLORS = {
  primary: [231, 131, 29] as [number, number, number], // brand primary
  ink: [28, 28, 32] as [number, number, number],
  muted: [110, 110, 118] as [number, number, number],
  hairline: [220, 218, 212] as [number, number, number],
  panel: [248, 244, 236] as [number, number, number],
};

function setFill(doc: jsPDF, c: [number, number, number]) {
  doc.setFillColor(c[0], c[1], c[2]);
}
function setStroke(doc: jsPDF, c: [number, number, number]) {
  doc.setDrawColor(c[0], c[1], c[2]);
}
function setText(doc: jsPDF, c: [number, number, number]) {
  doc.setTextColor(c[0], c[1], c[2]);
}

function ensureSpace(ctx: Ctx, needed: number) {
  if (ctx.y + needed > ctx.pageH - ctx.margin) {
    ctx.doc.addPage();
    ctx.y = ctx.margin;
    drawFooter(ctx);
  }
}

function drawFooter(ctx: Ctx) {
  const { doc, pageH, margin } = ctx;
  setText(doc, COLORS.muted);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  const page = doc.getNumberOfPages();
  doc.text(
    'Confidential — for the named leader only. Anonymized at k>=5.',
    margin,
    pageH - 18,
  );
  doc.text(`${page}`, ctx.w + margin - 4, pageH - 18, { align: 'right' });
}

function writeWrapped(
  ctx: Ctx,
  text: string,
  opts: { size: number; bold?: boolean; color?: [number, number, number]; gap?: number; indent?: number },
) {
  const { doc } = ctx;
  doc.setFont('helvetica', opts.bold ? 'bold' : 'normal');
  doc.setFontSize(opts.size);
  setText(doc, opts.color ?? COLORS.ink);
  const indent = opts.indent ?? 0;
  const lines = doc.splitTextToSize(text, ctx.w - indent);
  const lineH = opts.size * 1.35;
  ensureSpace(ctx, lines.length * lineH + (opts.gap ?? 0));
  for (const line of lines) {
    doc.text(line, ctx.x + indent, ctx.y + opts.size * 0.9);
    ctx.y += lineH;
  }
  ctx.y += opts.gap ?? 0;
}

function rule(ctx: Ctx, gap = 10) {
  ensureSpace(ctx, gap + 1);
  setStroke(ctx.doc, COLORS.hairline);
  ctx.doc.setLineWidth(0.4);
  ctx.doc.line(ctx.x, ctx.y, ctx.x + ctx.w, ctx.y);
  ctx.y += gap;
}

function sectionLabel(ctx: Ctx, label: string) {
  ensureSpace(ctx, 18);
  ctx.doc.setFont('helvetica', 'bold');
  ctx.doc.setFontSize(8);
  setText(ctx.doc, COLORS.primary);
  ctx.doc.text(label.toUpperCase(), ctx.x, ctx.y + 6);
  ctx.y += 14;
}

function bullets(ctx: Ctx, items: string[], opts?: { ordered?: boolean }) {
  items.forEach((item, i) => {
    const marker = opts?.ordered ? `${i + 1}.` : '•';
    ctx.doc.setFont('helvetica', 'bold');
    ctx.doc.setFontSize(10);
    setText(ctx.doc, COLORS.primary);
    const lines = ctx.doc.splitTextToSize(item, ctx.w - 18);
    const lineH = 10 * 1.4;
    ensureSpace(ctx, lines.length * lineH + 4);
    ctx.doc.text(marker, ctx.x, ctx.y + 9);
    ctx.doc.setFont('helvetica', 'normal');
    setText(ctx.doc, COLORS.ink);
    lines.forEach((ln: string, idx: number) => {
      ctx.doc.text(ln, ctx.x + 16, ctx.y + 9 + idx * lineH);
    });
    ctx.y += lines.length * lineH + 4;
  });
}

function panel(ctx: Ctx, render: (innerCtx: Ctx) => void) {
  const padding = 12;
  const startY = ctx.y;
  const startPage = ctx.doc.getNumberOfPages();
  // Reserve top padding before rendering content
  const innerCtx: Ctx = {
    ...ctx,
    x: ctx.x + padding,
    y: ctx.y + padding,
    w: ctx.w - padding * 2,
  };
  render(innerCtx);
  const endPage = ctx.doc.getNumberOfPages();
  const endY = innerCtx.y + padding;

  // Only draw the surrounding card when the content fit on a single page.
  // Otherwise skip decoration — content remains fully readable across pages.
  if (startPage === endPage) {
    setStroke(ctx.doc, COLORS.hairline);
    ctx.doc.setLineWidth(0.5);
    // Stroke-only outline so the box never paints over the text inside it.
    ctx.doc.roundedRect(ctx.x, startY, ctx.w, endY - startY, 6, 6, 'S');
    // Brand accent bar on the left edge
    setFill(ctx.doc, COLORS.primary);
    ctx.doc.rect(ctx.x, startY, 3, endY - startY, 'F');
  }
  ctx.y = endY + 6;
}

function header(ctx: Ctx, data: LeaderInsights, surveyName: string) {
  const { doc } = ctx;
  // brand bar
  setFill(doc, COLORS.primary);
  doc.rect(0, 0, ctx.w + ctx.margin * 2, 6, 'F');

  ctx.y = ctx.margin + 4;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  setText(doc, COLORS.muted);
  doc.text('CONFIDENTIAL LEADER INSIGHTS', ctx.x, ctx.y);
  ctx.y += 18;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  setText(doc, COLORS.ink);
  const titleLines = doc.splitTextToSize(surveyName || 'Leader Insights', ctx.w);
  titleLines.forEach((ln: string) => {
    doc.text(ln, ctx.x, ctx.y);
    ctx.y += 22;
  });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  setText(doc, COLORS.muted);
  const generated = new Date(data.generated_at).toLocaleString();
  const meta = [
    `Generated ${generated}`,
    `${data.distinct_employees} distinct respondent${data.distinct_employees === 1 ? '' : 's'}`,
    `k>=5 anonymity ${data.k_floor_met ? 'met' : 'not met (quantitative only)'}`,
  ].join('   •   ');
  doc.text(meta, ctx.x, ctx.y);
  ctx.y += 14;

  rule(ctx, 14);
}

export function buildLeaderInsightsPdf(data: LeaderInsights, surveyName: string): jsPDF {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const margin = 56;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const ctx: Ctx = {
    doc,
    x: margin,
    y: margin,
    w: pageW - margin * 2,
    pageH,
    margin,
  };

  header(ctx, data, surveyName);

  if (data.note) {
    writeWrapped(ctx, data.note, { size: 10, color: COLORS.muted, gap: 8 });
  }

  if (data.systemic_insight) {
    sectionLabel(ctx, 'Systemic insight');
    panel(ctx, (inner) => {
      writeWrapped(inner, data.systemic_insight ?? '', { size: 11, gap: 0 });
    });
  }

  if (data.priorities?.length) {
    sectionLabel(ctx, 'Prioritize first');
    bullets(ctx, data.priorities, { ordered: true });
    ctx.y += 4;
  }

  if (data.per_anchor?.length) {
    sectionLabel(ctx, 'Per-anchor breakdown');
    data.per_anchor.forEach((a, idx) => {
      ensureSpace(ctx, 60);
      if (idx > 0) ctx.y += 4;

      // anchor card
      panel(ctx, (inner) => {
        writeWrapped(inner, a.name, { size: 13, bold: true, gap: 6 });

        if (a.themes?.length) {
          writeWrapped(inner, 'Themes', { size: 8, bold: true, color: COLORS.muted, gap: 2 });
          bullets(inner, a.themes);
        }
        if (a.suggested_actions?.length) {
          inner.y += 2;
          writeWrapped(inner, 'Suggested actions', {
            size: 8,
            bold: true,
            color: COLORS.muted,
            gap: 2,
          });
          bullets(inner, a.suggested_actions);
        }
        if (a.reflection_prompts?.length) {
          inner.y += 2;
          writeWrapped(inner, 'Reflection prompts', {
            size: 8,
            bold: true,
            color: COLORS.muted,
            gap: 2,
          });
          a.reflection_prompts.forEach((q) => {
            writeWrapped(inner, `“${q}”`, {
              size: 10,
              color: COLORS.muted,
              gap: 2,
              indent: 4,
            });
          });
        }
      });
    });
  }

  // Footer on every page
  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    doc.setPage(p);
    drawFooter(ctx);
  }

  return doc;
}

export function downloadLeaderInsightsPdf(data: LeaderInsights, surveyName: string) {
  const doc = buildLeaderInsightsPdf(data, surveyName);
  const safe = (surveyName || 'leader-insights').replace(/[^a-z0-9-]+/gi, '-').toLowerCase();
  doc.save(`${safe}-leader-insights.pdf`);
}
